import { MapMode } from '@pn/core/domain/map';
import { Frequency } from '@pn/core/domain/production/frequency';
import { TableTemplateSchema } from '@pn/core/domain/table';
import { UnitSystem } from '@pn/core/domain/types';
import {
  ChartScale,
  ChartStreamStylesSchema,
  displayModes,
} from '@pn/services/charts/types';
import { z } from 'zod';

export const UserSettingsSchema = z.object({
  unitSystem: z.enum(UnitSystem),
  mapMode: z.enum(MapMode),
  tableFields: z.record(z.string(), z.array(z.string()).optional()),
  selectedTemplates: z.record(z.string(), TableTemplateSchema.optional()),
  chartConfig: z.object({
    displayMode: z.enum(displayModes),
    scale: z.enum(ChartScale),
    frequency: z.enum(Frequency),
    streams: z.record(z.string(), z.boolean()),
    streamPreferences: z.record(z.string(), z.boolean()),
    streamStyles: ChartStreamStylesSchema,
  }),
  grids: z.object({
    township: z.boolean(),
    section: z.boolean(),
    quarterSection: z.boolean(),
    lsd: z.boolean(),
    block: z.boolean(),
    unit: z.boolean(),
    county: z.boolean(),
    abstract: z.boolean(),
  }),
  baseLayers: z.object({
    hydrology: z.boolean(),
    landUse: z.boolean(),
    admin: z.boolean(),
    roads: z.boolean(),
    structures: z.boolean(),
    poiLabels: z.boolean(),
    elevationContours: z.boolean(),
  }),
  pnLayerStyles: z.record(z.string(), z.array(z.any())), // TODO: implement Layer schema
});

export type UserSettings = z.infer<typeof UserSettingsSchema>;

/* Local storage key for the last saved settings, used as an API fallback. */
export const USER_SETTINGS_STORAGE_KEY = 'user-settings';

export const defaultWellFluids = ['Oil', 'Gas', 'Water', 'Condensate'];

export const defaultUserSettings: UserSettings = {
  unitSystem: UnitSystem.Metric,
  mapMode: MapMode.Standard,
  tableFields: {},
  selectedTemplates: {},
  chartConfig: {
    displayMode: 'chart',
    scale: ChartScale.Logarithmic,
    frequency: Frequency.Monthly,
    streams: {},
    streamPreferences: Object.fromEntries(
      defaultWellFluids.map((fluid) => [fluid, true])
    ),
    streamStyles: {},
  },
  grids: {
    township: true,
    section: true,
    quarterSection: false,
    lsd: true,
    block: true,
    unit: true,
    county: true,
    abstract: true,
  },
  baseLayers: {
    hydrology: true,
    landUse: false,
    admin: true,
    roads: true,
    structures: false,
    poiLabels: true,
    elevationContours: false,
  },
  pnLayerStyles: {},
};
